import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, inject, output } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_FORMATS, MatOptionSelectionChange, MatOption, MatOptgroup } from '@angular/material/core';

import { CvSectionForm } from '../cv-creation.component';
import { CvCreationService } from '../cv-creation.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CvMapFilterPipe } from '../../../shared/pipes/cv-map-filter.pipe';
import { TranslateObjectPipe } from '../../../shared/pipes/translate-object.pipe';
import { MatButton } from '@angular/material/button';
import { EspDatepickerComponent } from '../../../bits/esp-datepicker/esp-datepicker.component';
import { SvgViewboxDirective } from '@exl-ng/mulo-core';
import { MatChipSet, MatChip, MatChipRemove } from '@angular/material/chips';
import { MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { DropListDirective, DragDirective, ExpansionPanelComponent, ContextFrameComponent, FacetChipDirective } from '@exl-ng/mulo-common';
import { CdkAccordion } from '@angular/cdk/accordion';


export const YEAR_ONLY_FORMAT = {
    parse: {
        dateInput: 'YYYY',
    },
    display: {
        dateInput: 'YYYY',
    },
};

@Component({
    selector: 'esp-cv-info',
    templateUrl: './cv-info.component.html',
    styleUrls: ['./cv-info.component.scss'],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: YEAR_ONLY_FORMAT }],
    imports: [
        CdkAccordion,
        DropListDirective,
        DragDirective,
        ExpansionPanelComponent,
        FormsModule,
        ReactiveFormsModule,
        MatSlideToggle,
        ContextFrameComponent,
        MatIcon,
        MatFormField,
        MatLabel,
        MatInput,
        MatSelect,
        MatOption,
        MatOptgroup,
        MatChipSet,
        MatChip,
        MatChipRemove,
        FacetChipDirective,
        SvgViewboxDirective,
        EspDatepickerComponent,
        MatButton,
        TranslateModule,
        TranslateObjectPipe,
        CvMapFilterPipe
    ]
})
export class CvInfoComponent {
    cvCreationService = inject(CvCreationService);
    private _t = inject(TranslateService);

    readonly nextStep = output<void>();
    a11yEditing = false;
    a11yEditingStart: number;
    a11yEditingEnd: number;

    // TODO: check if logic of this property can be removed
    private numberOfStickySections = 0;
    private _sections: FormArray<FormGroup<CvSectionForm>>;

    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input()
    get sections(): FormArray<FormGroup<CvSectionForm>> {
        return this._sections;
    }

    set sections(sections: FormArray<FormGroup<CvSectionForm>>) {
        this._sections = sections;
        this.addDefaultValues();
        this.numberOfStickySections = sections.value.filter(
            (section) => section.order <= 0
        ).length;
    }

    trackBy = (index: number, item: any) => item.value.id;

    getEditBtnLabel(section) {
        const selected = section?.value?.primaryFilter || [];
        const translated = selected.map((_) => this._t.instant(_));
        return translated.join(', ');
    }

    addDefaultValues() {
        // TODO: need an infratructure
        this.sections?.controls?.forEach((section) => {
            const defaultFormat =
                this.cvCreationService.optionsMap?.[section.value.entryType]
                    ?.formatOptionList?.[0]?.code ?? '';
            section.controls.format?.patchValue(
                section.value.format || defaultFormat
            );
        });
    }

    optionSelectionChange(option: MatOptionSelectionChange, section) {
        if (
            option.isUserInput &&
            !['research.cv.grant', 'research.cv.project'].includes(
                section.value.entryType
            )
        ) {
            const secondaryList: string[] =
                this.cvCreationService.optionsMap[section.value.entryType]
                    ?.secondaryFilterMap[option.source.value];
            if (option.source.selected) {
                section.controls.secondaryFilter.patchValue([
                    ...section.value.secondaryFilter,
                    ...secondaryList,
                ] as any);
            } else {
                const list = section.value.secondaryFilter.filter(
                    (v) => !secondaryList.includes(v)
                );
                section.controls.secondaryFilter.patchValue(list);
            }
        }
    }

    removeSecondaryFilter(
        filterCode: string,
        section: FormGroup<CvSectionForm>
    ) {
        const index = section.value.secondaryFilter.indexOf(filterCode);

        if (index >= 0) {
            const filterdList = (
                section.value.secondaryFilter as unknown as string[]
            ).filter((item) => filterCode !== item);
            section.controls.secondaryFilter.patchValue([
                ...filterdList,
            ] as any);
        }
    }

    drop(item) {
        const ctrls = this._sections.controls;
        const previousIndex = item.previousIndex + this.numberOfStickySections;
        const currentIndex = item.currentIndex + this.numberOfStickySections;
        moveItemInArray(ctrls, previousIndex, currentIndex);
        ctrls.forEach((ctrl, i) => this._sections.setControl(i, ctrl));

        if (this.a11yEditing) {
            if (this.a11yEditingStart === undefined) {
                this.a11yEditingStart = previousIndex;
            }
            this.a11yEditingEnd = currentIndex;
        }
    }

    onA11yEditing(ev) {
        this.a11yEditing = ev;
        if (!ev) {
            this.a11yEditingStart = undefined;
            this.a11yEditingEnd = undefined;
        }
    }

    handleDateChange(
        dateStr: string,
        ctrl: AbstractControl,
        fieldName: string
    ) {
        ctrl.patchValue(
            { ...ctrl.value, [fieldName]: dateStr },
            { emitEvent: false }
        );
    }
}
